/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import {
  BOYS_AND_MEN,
  BOYS_OR_MEN,
  BOYS_OR_MEN_MIXED,
  EMPTY_STRING,
  GIRLS_AND_WOMEN_MIXED,
  GIRLS_OR_WOMEN,
  GIRLS_OR_WOMEN_MIXED,
} from '../../const';
import playWithFormatter from '../../formatters/playWith';
import filterMenuStyle from '../FiltersMenu/styles';
import useStyles from './styles';

const DELAY = 0;

const mixedValueMap = {
    1: { true: BOYS_OR_MEN_MIXED, false: BOYS_OR_MEN },
    4: { true: BOYS_OR_MEN_MIXED, false: BOYS_OR_MEN },
    2: { true: GIRLS_OR_WOMEN_MIXED, false: GIRLS_OR_WOMEN },
    5: { true: GIRLS_OR_WOMEN_MIXED, false: GIRLS_OR_WOMEN },
  };

const FilterByGender = ({
  onSave,
  currentValue: value,
  onErrorChange,
  filterShowHide,
}) => {
  const styles = useStyles();
  const filterMenuStyles = filterMenuStyle();

  const [currentValue, setCurrentValue] = useState(value);
  // To keep track of the last saved value
  const lastSavedValue = useRef(value || '');
  const formattedValue = playWithFormatter(currentValue);

  const selectedValue =
    currentValue === BOYS_OR_MEN_MIXED ||
    currentValue === GIRLS_OR_WOMEN_MIXED ||
    currentValue === GIRLS_AND_WOMEN_MIXED;

  // Handle input change
  const handleGenderChange = e => {
    const val = Number(e.target.value);
    setCurrentValue(val);
  };

  // Call onSave only after currentValue updates
  useEffect(() => {
    if (currentValue !== lastSavedValue.current) {
      onSave({ PlayWith: currentValue });
      lastSavedValue.current = currentValue;
    }
  }, [currentValue, onSave]);

  const [isMixed, setIsMixed] = useState(selectedValue);
  // if any changes in mixed selection
  useEffect(() => {
    let timeout;
    if (formattedValue) {
      timeout = setTimeout(() => {
        setIsMixed(selectedValue);
      }, DELAY);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [formattedValue, selectedValue]);

  const getMixedValue = useCallback((currentValue, isMixed) => {
    let value = EMPTY_STRING;

    if (mixedValueMap[currentValue]) {
      value = mixedValueMap[currentValue][isMixed];
    } else {
      value = isMixed ? GIRLS_AND_WOMEN_MIXED : BOYS_AND_MEN;
    }
    
    return value;
  },[])

  // set gender value
  const setMixedValue = useCallback(
    isMixed => {
      setCurrentValue(getMixedValue(currentValue, isMixed));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentValue, getMixedValue]
  );

  // for clear filter Sync local state with prop changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <Fragment>
      <div css={filterMenuStyles.filterHead}>
        <h5 css={filterMenuStyles.filterTitle}>Sex</h5>
      </div>
      <div css={filterMenuStyles.filterBody}>
        <div css={styles.radioButtons}>
          <input
            type="radio"
            id="any"
            name="gender"
            value={isMixed ? 7 : 6}
            checked={currentValue === (isMixed ? 7 : 6)}
            onChange={handleGenderChange}
            css={styles.radioInput}
            tabIndex={filterShowHide ? 0 : -1}
          />
          <label
            htmlFor="any"
            css={[
              styles.radioLabel,
              currentValue === (isMixed ? 7 : 6) && styles.radioLabelChecked,
            ]}
          >
            <span css={styles.labelText}>Any</span>
          </label>

          <input
            type="radio"
            id="male-only"
            name="gender"
            value={isMixed ? 4 : 1}
            checked={currentValue === (isMixed ? 4 : 1)}
            onChange={handleGenderChange}
            css={styles.radioInput}
            tabIndex={filterShowHide ? 0 : -1}
          />
          <label
            htmlFor="male-only"
            css={[
              styles.radioLabel,
              currentValue === (isMixed ? 4 : 1) && styles.radioLabelChecked,
            ]}
          >
            <span css={styles.labelText}>Male only</span>
          </label>

          <input
            type="radio"
            id="female-only"
            name="gender"
            value={isMixed ? 5 : 2}
            checked={currentValue === (isMixed ? 5 : 2)}
            onChange={handleGenderChange}
            css={styles.radioInput}
            tabIndex={filterShowHide ? 0 : -1}
          />
          <label
            htmlFor="female-only"
            css={[
              styles.radioLabel,
              currentValue === (isMixed ? 5 : 2) && styles.radioLabelChecked,
            ]}
          >
            <span css={styles.labelText}>Female only</span>
          </label>
        </div>

        <label
          htmlFor="mixed-options-toggle"
          css={styles.mixedOptions(isMixed)}
        >
          <span>Include mixed football options</span>
          <input
            type="checkbox"
            name="gender_mixed"
            id="mixed-options-toggle"
            checked={isMixed}
            onChange={e => {
              const newMixedValue = e.target.checked;
              setIsMixed(newMixedValue);
              setMixedValue(newMixedValue);
            }}
            css={styles.mixedCheckbox}
            tabIndex={filterShowHide ? 0 : -1}
          />
          <span />
        </label>
      </div>
    </Fragment>
  );
};

FilterByGender.propTypes = {
  currentValue: PropTypes.number,
  onSave: PropTypes.func,
};

export default FilterByGender;
